import { showToast } from "@/ts/modals";
import {
    getRolePermissions,
    getSavedRolePermissions,
    getUserRoles,
    updateRolePermissions,
} from "@/ts/retrieve-data";
import { Button, Checkbox, FormInstance, Table, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useMemo, useState } from "react";
import { UserOutlined } from "@ant-design/icons";
import usePermissionCheck from "@/hooks/usePermissionCheck";
import { ISavedPermissionsData, IUserPrivilegesState, IUserRolesComponentProps } from "@/ts/common-types";

const { Title } = Typography;



const UserRolesComponent: React.FC<IUserRolesComponentProps> = ({
    privilegesModalDisp = false,
    setPrivilegesModalDisp,
    clientId = [],
    userPrivileges = {},
    setUserPrivileges,
    selectedRole = 0,
    form,
}) => {
    const [savedPermissionData, setSavedPermissionData] = useState([]);

    const [permissionData, setPermissionData] = useState([]);

    const [availableRoles, setAvailableRoles] = useState([]);

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setIsLoading(true);
        Promise.all([
            getRolePermissions(),
            getSavedRolePermissions(),
            getUserRoles(),
        ])
            .then(([permissionsData, savedPermData, rolesData]) => {
                try {
                    setPermissionData(permissionsData);
                    setSavedPermissionData(savedPermData);

                    if (privilegesModalDisp) {
                        const privilegeObject = userPrivileges[clientId.toString()] || [];

                        if (Array.isArray(privilegeObject) && privilegeObject.length > 0) {
                            setSelectedPermissions(() =>
                                privilegeObject.map((permId: string) => ({
                                    role_id: selectedRole,
                                    permission_id: +permId,
                                }))
                            );
                        } else {
                            const selectedRolePermissions = savedPermData.filter(
                                (perm: ISavedPermissionsData) => perm.role_id === selectedRole
                            );
                            setSelectedPermissions(selectedRolePermissions);
                        }
                    } else {
                        setSelectedPermissions(savedPermData);
                    }

                    setAvailableRoles(rolesData);
                    setIsLoading(false);
                } catch (err) {
                    showToast("danger", "Error setting previleges");
                    setIsLoading(false);
                }
            })
            .catch((error) => {
                setIsLoading(false);
            });
    }, [privilegesModalDisp, selectedRole]);




    useEffect(() => {
        if (!privilegesModalDisp) {
            setSelectedPermissions(savedPermissionData);
        }
    }, [savedPermissionData, privilegesModalDisp]);

    const displayRoles = privilegesModalDisp
        ? availableRoles.filter((role) => selectedRole === role.role_id)
        : availableRoles;

    const [selectedPermissions, setSelectedPermissions] = useState<ISavedPermissionsData[]>([]);



    const handleCheckboxChange = (
        module_name: string,
        permission_id: number,
        role_id: number
    ) => {
        setSelectedPermissions((prev: ISavedPermissionsData[]) => {
            const exists = prev.some(
                (p) =>
                    p.role_id === role_id && p.permission_id === permission_id
            );

            if (exists) {
                return prev.filter(
                    (p) =>
                        !(
                            p.role_id === role_id &&
                            p.permission_id === permission_id
                        )
                );
            }

            return [...prev, { role_id, permission_id }];
        });
    };

    const handleSaveChanges = async () => {
        setIsSaving(true);
        try {
            if (privilegesModalDisp) {

                const selectedRolePermissions = savedPermissionData.filter(
                    (perm: ISavedPermissionsData) => perm.role_id === selectedRole
                );

                let newPrivileges: string[] = []
                const updatedPrivileges = [
                    ...new Set(
                        selectedPermissions.map((p) => p.permission_id.toString())
                    ),
                ];

                if (updatedPrivileges.length !== selectedRolePermissions.length) {
                    newPrivileges = updatedPrivileges
                }

                const clientKey = Array.isArray(clientId)
                    ? clientId.join(",")
                    : clientId.toString();

                setUserPrivileges((prev) => ({
                    ...prev,
                    [clientKey]: newPrivileges,
                }));

                setIsSaving(false);
                setPrivilegesModalDisp(false);
            } else {
                // For User Roles tab - Global permissions management
                const payload = Object.values(permissionData)
                    .flat()
                    .reduce((acc, permission) => {
                        acc[permission.permission_id] = {};
                        availableRoles.forEach((role) => {
                            acc[permission.permission_id][role.role_id] =
                                selectedPermissions.some(
                                    (sp) =>
                                        sp.permission_id ===
                                            permission.permission_id &&
                                        sp.role_id === role.role_id
                                );
                        });
                        return acc;
                    }, {});

                const response = await updateRolePermissions(payload);
                if (response.status) {
                    showToast("success", response.message);

                    const newSavedPermissionData = Object.entries(
                        payload
                    ).flatMap(([permissionId, roles]) =>
                        Object.entries(roles as Record<string, boolean>)
                            .filter(([_, isChecked]) => isChecked)
                            .map(([roleId, _]) => ({
                                permission_id: parseInt(permissionId),
                                role_id: parseInt(roleId),
                            }))
                    );

                    setSavedPermissionData(newSavedPermissionData);
                    setSelectedPermissions(newSavedPermissionData);
                } else {
                    showToast("danger", response.message);
                }
            }
        } catch (error) {
            console.error("Error updating role permissions:", error);
            showToast("danger", "Failed to update role permissions");
        } finally {
            setIsSaving(false);
        }
    };

    // Transform permission data for table
    const tableData = useMemo(
        () =>
            Object.entries(permissionData).flatMap(
                ([module_name, permissions]) => [
                    // Add module header row
                    {
                        key: `header_${module_name}`,
                        title: module_name,
                        isModuleHeader: true,
                        module_name,
                        permission_id: null,
                    },
                    // Add permission rows
                    ...permissions.map((permission: any) => ({
                        key: `${module_name}_${permission.permission_id}`,
                        title: permission.title,
                        permission_id: permission.permission_id,
                        module_name,
                        isModuleHeader: false,
                        ...displayRoles.reduce(
                            (acc, role) => ({
                                ...acc,
                                [`role_${role.role_id}`]: null,
                            }),
                            {}
                        ),
                    })),
                ]
            ),
        [permissionData, displayRoles]
    );

    // Modify columns to handle module headers
    const columns = useMemo(() => {
        const baseColumns: ColumnsType<any> = [
            {
                title: "Actions",
                dataIndex: "title",
                fixed: "left" as const,
                width: 250,
                render: (text: string, record: any) =>
                    record.isModuleHeader ? (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                fontWeight: 500,
                            }}
                        >
                            <UserOutlined
                                style={{ color: "#1677ff", marginRight: 8 }}
                            />
                            <Title level={5} style={{ margin: 0 }}>
                                {text}
                            </Title>
                        </div>
                    ) : (
                        text
                    ),
            },
        ];

        const roleColumns: ColumnsType<any> = displayRoles.map((role) => ({
            title: role.role_name,
            dataIndex: `role_${role.role_id}`,
            width: 150,
            render: (_: any, record: any) =>
                !record.isModuleHeader && (
                    <Checkbox
                        onChange={() =>
                            handleCheckboxChange(
                                record.module_name,
                                record.permission_id,
                                role.role_id
                            )
                        }
                        checked={selectedPermissions.some(
                            (ele) =>
                                ele.role_id === role.role_id &&
                                ele.permission_id === record.permission_id
                        )}
                    />
                ),
        }));

        return [...baseColumns, ...roleColumns];
    }, [displayRoles, selectedPermissions]);

    const { canUpdatePermissions } = usePermissionCheck()

    return (
        <div>
            {privilegesModalDisp ? null : <h5>User Roles</h5>}

            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "calc(100vh - 200px)",
                    marginTop: "1rem",
                    position: "relative",
                }}
            >
                <div
                    className={`table-container ${!canUpdatePermissions ? 'pe-none opacity-50' : ''}`}
                    style={{
                        overflowX: "auto",
                        overflowY: "auto",
                        position: "relative",
                        height: "calc(100vh - 300px)",
                    }}
                >
                    <Table
                        loading={isLoading}
                        columns={columns}
                        dataSource={tableData}
                        pagination={false}
                        scroll={{
                            x: displayRoles.length * 150 + 250,
                            y: "calc(100vh - 350px)",
                        }}
                        bordered
                        size="middle"
                        rowClassName={(record) =>
                            record.isModuleHeader ? "module-header-row" : ""
                        }
                        sticky
                    />
                </div>
                <div
                    style={{
                        position: "sticky",
                        bottom: "60px",
                        backgroundColor: "white",
                        padding: "2px",
                        borderTop: "1px solid #e8e8e8",
                        textAlign: "right",
                        zIndex: 10,
                        width: "100%",
                    }}
                >
                    <Button 
                        type="primary" 
                        onClick={handleSaveChanges}
                        loading={isSaving}
                        disabled={!canUpdatePermissions}
                    >
                        {isSaving ? 'Saving changes...' : 'Save changes'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default UserRolesComponent;
